import React, { useEffect, useState } from 'react';
import moment from 'moment';

/** Plugins */
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CircularProgress, Button } from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon, ThumbUp, AddBox } from '@material-ui/icons';

/** Components */
import DataTable from '../../components/Table/DataTable';
import SearchBox from './../../components/SearchBox/SearchBox';
import AdminModal from '../../components/AdminModal/AdminModal';

/** Constants */
import Languages from '../../constants/languages';

/** Services */
import {
  getChallenges,
  createChallenge,
  updateChallenge,
  enableChallenge,
  approveChallenge,
  getChallengesBySearch,
  getChallengesByOrder,
} from '../../services/challenges';

/** Actions */
import { showAlert } from '../../store/ducks/alert';


const Challenges = ({ location, match }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [pageData, setPageData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchField, setSearchField] = useState({
    searchItem: '',
    search: '',
  });
  const [isOrdering, setIsOrdering] = useState(0);
  const [orderDirections, setOrderDirections] = useState({
    item: '',
    direction: '',
  });

  useEffect(() => {
    if (location.state && location.state.remount) {
      handleSubmitSearch({
        searchItem: '',
        search: '',
      });
    }
  }, [location]);

  useEffect(() => {
    if (match && match.params.searchItem && match.params.search) {
      handleSubmitSearch({
        searchItem: match.params.searchItem,
        search: match.params.search,
      });
    }
    displayPageData();
  }, [isSearching, isOrdering, page]);

  const handleSubmitSearch = searchBox => {
    setSearchField({
      ...searchField,
      searchItem: searchBox.searchItem,
      search: searchBox.search,
    });
    setIsSearching(searchBox.search);
  };

  const displayPageData = async () => {
    try {
      setIsLoading(true);

      let pageData;
      if (isSearching) {
        const { data } = await getChallengesBySearch(searchField.searchItem, searchField.search);
        pageData = data;
      } else if (isOrdering > 0) {
        const { data } = await getChallengesByOrder(orderDirections.item, orderDirections.direction);
        pageData = data;
      } else if (!isOrdering && !isSearching && Object.keys(match.params).length === 0) {
        const { data } = await getChallenges(page);
        pageData = data;
      }
      if (!pageData) return;
      
      for (let item in pageData.rows){
        const i18n = pageData.rows[item].i18nContent;
        pageData.rows[item].i18n = i18n;
        delete pageData.rows[item].i18nContent;
      }
      setPageData(pageData.rows);
      setTotalItems(pageData.count);
      setTotalPages(pageData.pages);
      setPage(pageData.page);
      setIsLoading(false);
    } catch (error) {
      dispatch(showAlert('error', error.message));
    }
  };

  const customActions = [
    {
      icon: <ThumbUp />,
      action: item => {
        handleApproveChallenge(item.id);
      },
    },
    {
      icon: <EditIcon />,
      action: item => {
        handleOpenUpdate(item);
      },
    },
    {
      icon: <AddBox />,
      action: item => {
        handleEnable(item);
      },
    },
  ];

  const headers = ['id', 'name', 'winnersCount', 'createdAt', 'untilDate', 'approved', 'enabled'];

  const renderLangs = () => {
    let aux = {};
    Languages.map(lang => {
      aux[lang.code] = {
        title: '',
        subtitle: '',
        description: '',
        prize: '',
        rules: ''
      };
    });
    return aux;
  };

  const modalColumns = [
    { label: 'SQL Query', prop: 'sqlCheck', type: 'text', disabled: isUpdating, required: true },
    { label: 'SQL Progress', prop: 'sqlCheckProgress', type: 'text', disabled: isUpdating },
    { label: 'Limit', prop: 'limit', type: 'number', required: true },
    { label: 'Start Date', prop: 'startDate', type: 'date', disablePast: true, defaultValue: moment(), required: true },
    { label: 'Until Date', prop: 'untilDate', type: 'date', disablePast: true, defaultValue: moment().add(12, 'month'), required: true },
    { label: 'Name', prop: 'name', type: 'text', required: true },
    { label: 'Prize', prop: 'prize', type: 'text', required: true },
    {
      label: 'Intertionalization',
      prop: 'i18n',
      type: 'i18',
      defaultValue: renderLangs(),
    },
  ];

  const handleApproveChallenge = async id => {
    try {
      await approveChallenge(id, true);
      dispatch(showAlert('success', 'Success, approved! 🔥'));
      window.location = '/challenges';
    } catch (error) {
      dispatch(showAlert('error', 'Error! Something wrong happened. 😔'));
    }
  };

  const handleCloseModal = () => {
    setFormData({});
    setOpen(false);
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleOpenUpdate = async item => {
    setIsUpdating(true);
    setFormData(item);
    setOpen(true);
  };

  const handleEnable = async item => {
    try {
      if (item.enabled === '⛔️ Não') await enableChallenge(item.id, true);
      if (item.enabled === '✅ Sim') await enableChallenge(item.id, false);
      dispatch(showAlert('success', 'Success! 🔥'));
      window.location = '/challenges';
    } catch (error) {
      dispatch(showAlert('error', 'Error! Something went wrong. 😔'));
    }
  };

  const handleSubmitForm = async dataForm => {
    try {
      if (!isUpdating) {
        await createChallenge(dataForm);
      } else {
        await updateChallenge(dataForm.id, dataForm);
      }
      setIsUpdating(false);

      dispatch(showAlert('success', 'Success! 🔥'));
      window.location = '/challenges';
    } catch (error) {
      dispatch(showAlert('error', 'Error! Something went wrong. 😔'));
    }
  };

  if (isLoading) return <CircularProgress color="inherit" className="circular-progress" />;

  return (
    <>
      <div className="channels-container">
        <h1> Challenges ({totalItems})</h1>
        <Button onClick={handleOpenModal} variant="contained" className="modal-btn">
          Add Challenge
          <AddIcon />
        </Button>
        {open ? (
          <AdminModal
            columns={modalColumns}
            data={formData}
            modalName={isUpdating ? "Update Challenge" : "Create Challenge"}
            onSubmit={handleSubmitForm}
            onClose={handleCloseModal}
            isUpdating={isUpdating}
          />
        ) : (
          ''
        )}

        <SearchBox selectItems={headers} onSubmitSearch={handleSubmitSearch} isSearching={isSearching} setIsOrdering={setIsOrdering} />
      </div>

      <DataTable
        head={headers}
        body={pageData}
        bodyColumn={headers}
        action={true}
        customActions={customActions}
        linkPath="challengeWinners"
        totalPages={totalPages}
        currentPage={page}
        setPageChange={setPage}
        orderDirections={orderDirections}
        setOrderDirections={setOrderDirections}
        setPageChange={setPage}
        setIsOrdering={setIsOrdering}
        isOrdering={isOrdering}
      />
    </>
  );
};

export default Challenges;
